import React from 'react';
import { Popover, Button, type PopoverProps } from '@blueprintjs/core-modern';
import { IconNames } from "@blueprintjs/icons";

export type FilterPopoverProps<T = React.HTMLProps<HTMLElement>> = Omit<PopoverProps<T>, 'children'> & {
  /** When set, renders a Button with this content as the popover trigger (string or React node, e.g. <strong>bold</strong> text). Otherwise `children` is the target. */
  activateText?: React.ReactNode;
  children?: React.ReactNode;
  showClear?: boolean;
  onClearDisabled?: boolean;
  onClear?: () => void;
};

/**
 * Filter-specific popover based on Blueprint Popover.
 * Styling: arrow is removed. Pass `activateText` to use a button as the trigger, or pass `children` for a custom target.
 */
export const FilterPopover: React.FC<FilterPopoverProps> = ({
  modifiers,
  popoverClassName,
  activateText,
  children,
  content,
  showClear = false,
  onClear,
  onClearDisabled = false,
  ...rest
}) => {
  const mergedModifiers = {
    ...modifiers,
    arrow: { ...modifiers?.arrow, enabled: false },
  };

  const updatedContent = (
    <>
      {content}
      {showClear && (
        <span className='modern-filter-popover-footer'>
          <Button
            className='modern-filter-popover-footer-button'
            style={{ width: '' }}
            icon={IconNames.CROSS}
            text="Clear"
            disabled={onClearDisabled}
            onClick={() => {
              if (onClear) {
                onClear();
              }
            }}
          />
        </span>
      )}
    </>
  );

  return (
    <Popover
      interactionKind="click"
      position="bottom-left"
      transitionDuration={0}
      autoFocus
      content={updatedContent}
      {...rest}
      modifiers={mergedModifiers}
      popoverClassName={popoverClassName ? `modern-filter-popover ${popoverClassName}` : 'modern-filter-popover'}
      renderTarget={
        activateText != null
          ? ({ isOpen, ref, ...targetProps }) => (
            <Button
              ref={ref as React.Ref<HTMLButtonElement>}
              {...targetProps}
              endIcon={isOpen ? 'chevron-up' : 'chevron-down'}
              className="modern-filter-popover-button"
              variant="minimal"
            >
              {activateText}
            </Button>
          )
          : undefined
      }
    >
      {activateText != null ? undefined : children}
    </Popover>
  );
};


FilterPopover.displayName = 'FilterPopover';
